import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import { useDispatch } from "react-redux";
import { Controller } from "react-hook-form";
import { showChildModal } from "../../data/modal";
import { useTranslation } from "react-i18next";

import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Grid,
  Typography,
  FormControl,
  Tooltip,
} from "@mui/material";

import {
  InsertDriveFileSharp as InsertDriveFileSharpIcon,
  ClearSharp as ClearSharpIcon,
  UploadFileSharp as UploadFileSharpIcon,
  Add as AddIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Image as ImageIcon,
} from "@mui/icons-material";

const joinUrl = (...parts: string[]) => {
  return parts
    .filter(Boolean)
    .map((part, index) => {
      if (index === 0) {
        return part.replace(/\/+$/g, "");
      }

      return part.replace(/^\/+/g, "").replace(/\/+$/g, "");
    })
    .join("/");
};

const getApiBaseUrl = () => {
  const rawApiUrl = (process.env.REACT_APP_API_URL || "").trim();

  if (!rawApiUrl) {
    return "/api";
  }

  const normalizedApiUrl = rawApiUrl.replace(/\/+$/g, "");

  if (normalizedApiUrl.endsWith("/api")) {
    return normalizedApiUrl;
  }

  return joinUrl(normalizedApiUrl, "api");
};

const getFilesUploadUrl = () => {
  return joinUrl(getApiBaseUrl(), "files");
};

const getFileName = (file: any) => {
  return (
    file?.description ??
    file?.discription ??
    file?.name ??
    file?.fileName ??
    file?.filename ??
    "File"
  );
};

const getFileContentUrl = (file: any) => {
  return file?.contentUrl ?? file?.url ?? "";
};

const getFileExtension = (file: any) => {
  const fileUrl = getFileContentUrl(file);
  const fileName = getFileName(file);
  const source = fileUrl || fileName;

  const cleanSource = source.split("?")[0].split("#")[0];
  const extension = cleanSource.split(".").pop();

  return extension ? extension.toLowerCase() : "";
};

const isImageFile = (file: any) => {
  const extension = getFileExtension(file);

  return ["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(extension);
};

const isPdfFile = (file: any) => {
  return getFileExtension(file) === "pdf";
};

const FileThumbnailIcon = ({ file }: any) => {
  if (isImageFile(file)) {
    return <ImageIcon fontSize="large" />;
  }

  if (isPdfFile(file)) {
    return <PictureAsPdfIcon fontSize="large" />;
  }

  return <InsertDriveFileSharpIcon fontSize="large" />;
};

const normalizeFileList = (files?: any[]) => {
  if (!Array.isArray(files)) {
    return [];
  }

  return files
    .filter(Boolean)
    .filter((file) => {
      if (file instanceof File) {
        return false;
      }

      return Boolean(file?.["@id"] || file?.id);
    });
};

const UploadingFilePreview = ({ file }: { file: File }) => {
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    if (!file.type.startsWith("image/")) {
      setPreviewUrl("");
      return;
    }

    const url = URL.createObjectURL(file);
    setPreviewUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  return previewUrl ? (
    <Box
      component="img"
      src={previewUrl}
      alt={file.name}
      sx={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
      }}
    />
  ) : (
    <FileThumbnailIcon file={file} />
  );
};

export const MultipleFileField: any = ({
  name,
  control,
  setValue,
  existingFiles,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const previewWrapperRef = useRef<HTMLDivElement | null>(null);

  const [previewColumns, setPreviewColumns] = useState(1);
  const [existingFilesData, setExistingFilesData] = useState<any[]>(
    normalizeFileList(existingFiles)
  );
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);

  useEffect(() => {
    const element = previewWrapperRef.current;

    if (!element) {
      return;
    }

    const updateColumns = () => {
      const width = element.getBoundingClientRect().width;

      if (width < 420) {
        setPreviewColumns(1);
        return;
      }

      if (width < 720) {
        setPreviewColumns(2);
        return;
      }

      setPreviewColumns(3);
    };

    updateColumns();

    const resizeObserver = new ResizeObserver(updateColumns);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const normalizedExistingFiles = normalizeFileList(existingFiles);

    setExistingFilesData(normalizedExistingFiles);
    setValue(name, normalizedExistingFiles, {
      shouldValidate: true,
      shouldDirty: false,
    });
  }, [existingFiles, name, setValue]);

  const uploadHandler = async (file: File) => {
    const formData = new FormData();

    formData.append("file", file);

    const response = await axios({
      method: "POST",
      url: getFilesUploadUrl(),
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      data: formData,
    });

    if (response.status === 200 || response.status === 201) {
      return response.data;
    }

    throw new Error(t("failedToUploadFile") || "Failed to upload file.");
  };

  const fileDropHandler = async (files: File[]) => {
    if (!files || files.length === 0) {
      return;
    }

    setUploadError(null);
    setUploadingFiles(files);

    try {
      const uploadedFilesData = await Promise.all(
        files.map((file) => uploadHandler(file))
      );

      const normalizedUploadedFiles = normalizeFileList(uploadedFilesData);
      const nextFiles = [...normalizedUploadedFiles, ...existingFilesData];

      setExistingFilesData(nextFiles);
      setValue(name, nextFiles, {
        shouldValidate: true,
        shouldDirty: true,
      });
    } catch (error: any) {
      console.error(error);
      setUploadError(
        error?.response?.data?.message ??
          error?.message ??
          t("failedToUploadFile") ??
          "Failed to upload file."
      );
    } finally {
      setUploadingFiles([]);
    }
  };

  const dropzone = useDropzone({
    onDrop: fileDropHandler,
    noKeyboard: true,
  });

  const fileRemoveHandler = (event: React.MouseEvent, fileId: string) => {
    event.preventDefault();
    event.stopPropagation();

    const filteredFiles = existingFilesData.filter(
      (file) =>
        file?.["@id"] !== fileId &&
        file?.id?.toString() !== fileId?.toString()
    );

    setExistingFilesData(filteredFiles);
    setValue(name, filteredFiles, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const fileView = (file: any) => {
    dispatch(
      showChildModal({
        title: t("viewFile"),
        description: getFileName(file),
        form: "fileview",
        formProps: {
          file,
          isChildModal: true,
        },
        size: "default",
      }) as any
    );
  };

  const hasFiles = existingFilesData.length > 0 || uploadingFiles.length > 0;

  const fileCardSx = {
    position: "relative",
    height: 126,
    width: "100%",
    borderRadius: 2,
    border: "1px solid",
    borderColor: "divider",
    overflow: "hidden",
    backgroundColor: "background.default",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition:
      "border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease",
    "&:hover": {
      borderColor: "primary.main",
      boxShadow: 2,
      transform: "translateY(-1px)",
    },
    "&:hover .file-remove-button": {
      opacity: 1,
    },
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <Typography variant="h6" className="form-fields-title">
          {t("files")}
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <FormControl size="small" sx={{ width: "100%" }}>
          <Controller
            control={control}
            name={name}
            defaultValue={[]}
            render={() => (
              <Box
                {...dropzone.getRootProps({
                  className: "dropzone",
                })}
                sx={{
                  width: "100%",
                  minHeight: "20vh",
                  border: "2px dashed",
                  borderColor: dropzone.isDragActive
                    ? "primary.main"
                    : "#DDDDDD",
                  borderRadius: 2,
                  backgroundColor: dropzone.isDragActive
                    ? "action.hover"
                    : "background.paper",
                  p: 2,
                  cursor: "pointer",
                  transition:
                    "border-color 0.2s ease, background-color 0.2s ease",
                  "&:hover": {
                    borderColor: "primary.main",
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <Box component="input" {...dropzone.getInputProps()} />

                <Box
                  ref={previewWrapperRef}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${previewColumns}, minmax(0, 1fr))`,
                    gap: 1.5,
                    width: "100%",
                  }}
                >
                  {existingFilesData.map((file, index) => {
                    const fileName = getFileName(file);
                    const contentUrl = getFileContentUrl(file);
                    const fileId =
                      file?.["@id"] ?? file?.id ?? `existing-file-${index}`;

                    return (
                      <Box
                        key={`file-view-existing-file-${fileId}`}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          fileView(file);
                        }}
                        sx={fileCardSx}
                      >
                        {isImageFile(file) && contentUrl ? (
                          <Box
                            component="img"
                            src={contentUrl}
                            alt={fileName}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              color: "text.secondary",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 0.5,
                            }}
                          >
                            <FileThumbnailIcon file={file} />
                          </Box>
                        )}

                        <Box
                          sx={{
                            position: "absolute",
                            left: 0,
                            right: 0,
                            bottom: 0,
                            px: 1,
                            py: 0.75,
                            background:
                              "linear-gradient(to top, rgba(0,0,0,0.75), rgba(0,0,0,0.1))",
                            color: "common.white",
                          }}
                        >
                          <Tooltip title={fileName}>
                            <Typography
                              variant="caption"
                              noWrap
                              sx={{
                                display: "block",
                                lineHeight: 1.2,
                              }}
                            >
                              {fileName}
                            </Typography>
                          </Tooltip>
                        </Box>

                        <IconButton
                          type="button"
                          className="file-remove-button"
                          size="small"
                          onClick={(event) =>
                            fileRemoveHandler(event, fileId.toString())
                          }
                          sx={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            width: 26,
                            height: 26,
                            opacity: {
                              xs: 1,
                              md: 0,
                            },
                            backgroundColor: "rgba(255,255,255,0.92)",
                            "&:hover": {
                              backgroundColor: "rgba(255,255,255,1)",
                            },
                          }}
                        >
                          <ClearSharpIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    );
                  })}

                  {uploadingFiles.map((file, index) => (
                    <Box
                      key={`uploading-file-${file.name}-${index}`}
                      sx={{
                        ...fileCardSx,
                        borderColor: "primary.main",
                        opacity: 0.85,
                        cursor: "default",
                        "&:hover": {
                          borderColor: "primary.main",
                          boxShadow: 1,
                          transform: "none",
                        },
                      }}
                    >
                      <UploadingFilePreview file={file} />

                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "rgba(255,255,255,0.45)",
                        }}
                      >
                        <CircularProgress size={28} />
                      </Box>

                      <Box
                        sx={{
                          position: "absolute",
                          left: 0,
                          right: 0,
                          bottom: 0,
                          px: 1,
                          py: 0.75,
                          background:
                            "linear-gradient(to top, rgba(0,0,0,0.75), rgba(0,0,0,0.1))",
                          color: "common.white",
                        }}
                      >
                        <Tooltip title={file.name}>
                          <Typography
                            variant="caption"
                            noWrap
                            sx={{
                              display: "block",
                              lineHeight: 1.2,
                            }}
                          >
                            {file.name}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </Box>
                  ))}

                  <Box
                    sx={{
                      height: 126,
                      width: "100%",
                      borderRadius: 2,
                      border: "1px dashed",
                      borderColor: "divider",
                      backgroundColor: "background.default",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "text.secondary",
                      cursor: "pointer",
                      transition:
                        "border-color 0.2s ease, color 0.2s ease, background-color 0.2s ease",
                      "&:hover": {
                        borderColor: "primary.main",
                        color: "primary.main",
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <AddIcon />
                    <Typography variant="caption" mt={0.5}>
                      {t("addFile") || t("files")}
                    </Typography>
                  </Box>

                  {!hasFiles ? (
                    <Box
                      sx={{
                        gridColumn: `1 / -1`,
                        minHeight: 90,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        color: "text.secondary",
                        gap: 1,
                      }}
                    >
                      <UploadFileSharpIcon fontSize="large" />

                      <Typography variant="body2">
                        {dropzone.isDragActive
                          ? t("dropFilesHere") || t("dragAndDropFiles")
                          : t("dragAndDropFiles")}
                      </Typography>
                    </Box>
                  ) : null}

                  {uploadError ? (
                    <Box sx={{ gridColumn: `1 / -1` }}>
                      <Alert severity="error">{uploadError}</Alert>
                    </Box>
                  ) : null}
                </Box>
              </Box>
            )}
          />
        </FormControl>
      </Grid>
    </Grid>
  );
};

export default MultipleFileField;